import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'
import useMutationUpdateBlacklistReport from '../../../../../api/hooks/useMutationUpdateBlacklistReport'
import { BlacklistQueryKeys } from '../../../../../keys'
import { GetBlacklistResponse } from '../../../../../type'

const formSchema = z.object({
	phoneNumber: z.string().min(1, 'กรุณาระบุเบอร์โทรศัพท์'),
	reason: z.string().min(1, 'กรุณาระบุเหตุผล'),
})

type FormValues = z.infer<typeof formSchema>

interface BlacklistEditModalProps {
	item: GetBlacklistResponse['data'][number]
}

export const BlacklistEditModal = ({ item }: BlacklistEditModalProps) => {
	const [open, setOpen] = useState(false)
	const queryClient = useQueryClient()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			phoneNumber: '',
			reason: '',
		},
	})

	useEffect(() => {
		if (open) {
			form.reset({
				phoneNumber: '',
				reason: '',
			})
		}
	}, [open])

	const { mutate: updateBlacklistReport, isPending: isUpdating } =
		useMutationUpdateBlacklistReport()

	const onSubmit = (values: FormValues) => {
		try {
			// For edit mode, only send phone and reason
			const updateData = {
				phoneNumber: values.phoneNumber,
				reason: values.reason,
			}

			updateBlacklistReport(
				{
					id: item.id,
					payload: updateData,
				},
				{
					onSuccess: () => {
						toast.success('เพิ่มรายงาน Blacklist สำเร็จ')
						queryClient.invalidateQueries({
							queryKey: [BlacklistQueryKeys.GetBlacklist],
						})
						form.reset()
						setOpen(false)
					},
					onError: (error: any) => {
						toast.error(error?.response?.data?.message || 'เกิดข้อผิดพลาด')
					},
				},
			)
		} catch {
			toast.error('เกิดข้อผิดพลาดในการส่งข้อมูล')
		}
	}

	return (
		<>
			<Button variant="outline" size="sm" onClick={() => setOpen(true)}>
				<Plus className="mr-1 h-4 w-4" />
				เพิ่มการรายงาน
			</Button>
			{open && (
				<Dialog open onOpenChange={setOpen}>
					<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
						<DialogHeader>
							<DialogTitle>
								เพิ่มรายงาน Blacklist ({item.accountNumber})
							</DialogTitle>
						</DialogHeader>

						<Form {...form}>
							<div className="space-y-4">
								<form
									onSubmit={form.handleSubmit(onSubmit)}
									className="space-y-4"
								>
									{/* Display read-only fields */}
									<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												เลขบัญชี
											</label>
											<div className="mt-1 text-sm">{item.accountNumber}</div>
										</div>
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												ชื่อบัญชี
											</label>
											<div className="mt-1 text-sm">{item.accountName}</div>
										</div>
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												ธนาคาร
											</label>
											<div className="mt-1 text-sm">
												{item.bankProviderName}
											</div>
										</div>
									</div>

									{/* Fields for adding new report */}
									<div className="space-y-4 border-t pt-4">
										<h4 className="font-medium">เพิ่มรายงานใหม่</h4>
										<FormField
											control={form.control}
											name="phoneNumber"
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														<RequiredIndicator /> เบอร์โทรศัพท์
													</FormLabel>
													<FormControl>
														<Input
															placeholder="เพิ่มเบอร์โทรศัพท์ที่เกี่ยวข้อง"
															{...field}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="reason"
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														<RequiredIndicator /> เหตุผล
													</FormLabel>
													<FormControl>
														<Textarea
															placeholder="ระบุเหตุผลในการรายงานบัญชีนี้"
															className="min-h-[100px]"
															{...field}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>

									<div className="flex justify-end gap-4 pt-4">
										<Button
											type="button"
											variant="outline"
											onClick={() => setOpen(false)}
										>
											ยกเลิก
										</Button>
										<Button type="submit" disabled={isUpdating}>
											{isUpdating ? 'กำลังเพิ่มรายงาน...' : 'เพิ่มรายงาน'}
										</Button>
									</div>
								</form>
							</div>
						</Form>
					</DialogContent>
				</Dialog>
			)}
		</>
	)
}
